/* eslint-disable react-hooks/exhaustive-deps */
'use client'

import { memo, useEffect, useMemo, useRef, useState } from 'react'
import type { FC } from 'react'
import styled from 'styled-components'
import { motion } from 'motion/react'

export interface VideoSlide {
  mp4: string
  webm?: string
}

type Props = {
  className?: string
  firstRowVideos: VideoSlide[]
  secondRowVideos: VideoSlide[]
  thirdRowVideos: VideoSlide[]
}

const LOOP_DURATION_S = 25
const VISIBILITY_TICK_MS = 250
const VIEWPORT_MARGIN_PX = 250

const isNonEmptyString = (v: unknown): v is string =>
  typeof v === 'string' && v.trim().length > 0

const detectSafari = () => {
  const ua = navigator.userAgent
  const isAppleWebKit = /AppleWebKit/i.test(ua)
  const isNotChrome = !/Chrome|Chromium|CriOS|Edg/i.test(ua)
  const isNotFirefox = !/FxiOS/i.test(ua)
  return isAppleWebKit && isNotChrome && isNotFirefox
}

const shouldPlay = (rect: DOMRect) => {
  const vw = window.innerWidth
  const vh = window.innerHeight

  const outside =
    rect.right < -VIEWPORT_MARGIN_PX ||
    rect.left > vw + VIEWPORT_MARGIN_PX ||
    rect.bottom < -VIEWPORT_MARGIN_PX ||
    rect.top > vh + VIEWPORT_MARGIN_PX

  return !outside
}

const safePause = (el: HTMLVideoElement) => {
  try {
    if (!el.paused) el.pause()
  } catch {
    // ignore
  }
}

/**
 * preload='none' means the browser should not fetch until we request it.
 * We mark each video as "primed" once we call load() the first time.
 */
const primeVideoForPlayback = (
  el: HTMLVideoElement,
  preloadModeRef: React.RefObject<HTMLVideoElement['preload']>
) => {
  if (preloadModeRef.current !== 'none') return
  if (el.dataset.primed === '1') return

  el.preload = 'metadata'
  el.load()
  el.dataset.primed = '1'
}

const safePlay = async (
  el: HTMLVideoElement,
  preloadModeRef: React.RefObject<HTMLVideoElement['preload']>
) => {
  try {
    if (!el.paused) return

    primeVideoForPlayback(el, preloadModeRef)

    const p = el.play()
    if (p && typeof p.then === 'function') await p
  } catch {
    // ignore
  }
}

const pauseAll = (videos: HTMLVideoElement[]) => {
  videos.forEach((v) => safePause(v))
}

const useAutoPauseOffscreenVideos = (
  rootRef: React.RefObject<HTMLElement>,
  preloadModeRef: React.RefObject<HTMLVideoElement['preload']>
) => {
  const lastStateRef = useRef(new WeakMap<HTMLVideoElement, boolean>())
  const videosRef = useRef<HTMLVideoElement[]>([])
  const tickingRef = useRef(false)
  const rafRef = useRef<number>(0)
  const timerRef = useRef<number | null>(null)
  const isHeaderVisibleRef = useRef(true)

  const stopTicking = () => {
    tickingRef.current = false

    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = 0
    }
  }

  const tick = () => {
    if (!isHeaderVisibleRef.current) return

    if (document.visibilityState !== 'visible') {
      pauseAll(videosRef.current)
      return
    }

    const vids = videosRef.current
    vids.forEach((v) => {
      const rect = v.getBoundingClientRect()
      const next = shouldPlay(rect)
      const prev = lastStateRef.current.get(v)

      if (prev === next) return
      lastStateRef.current.set(v, next)

      if (next) void safePlay(v, preloadModeRef)
      else safePause(v)
    })
  }

  const startTicking = () => {
    if (tickingRef.current) return
    tickingRef.current = true

    timerRef.current = window.setInterval(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(tick)
    }, VISIBILITY_TICK_MS)

    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(tick)
  }

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    // (13) Cache videos once (no querySelectorAll in each tick)
    videosRef.current = Array.from(root.querySelectorAll('video'))

    // Pause by default until first tick decides
    pauseAll(videosRef.current)

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        const isVisible = Boolean(entry?.isIntersecting)

        isHeaderVisibleRef.current = isVisible

        if (!isVisible) {
          pauseAll(videosRef.current)
          stopTicking()
          return
        }

        startTicking()
      },
      {
        root: null,
        rootMargin: '200px 0px 200px 0px',
        threshold: 0.01
      }
    )

    observer.observe(root)

    // Kick-start immediately; IO will stop it if needed
    startTicking()

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') {
        pauseAll(videosRef.current)
        return
      }

      if (isHeaderVisibleRef.current) startTicking()
    }

    const onResize = () => {
      if (isHeaderVisibleRef.current) startTicking()
    }

    window.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('resize', onResize)

    return () => {
      observer.disconnect()
      stopTicking()
      window.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('resize', onResize)
    }
  }, [rootRef, preloadModeRef])
}

const buildLoop = (videos: VideoSlide[]) => {
  const base = videos.filter((v) => isNonEmptyString(v.mp4))
  return [...base, ...base]
}

const AutoVideo = ({
  slide,
  preload
}: {
  slide: VideoSlide
  preload: HTMLVideoElement['preload']
}) => {
  const hasWebm = isNonEmptyString(slide.webm)

  return (
    <video
      muted
      autoPlay
      loop
      playsInline
      preload={preload}
      disablePictureInPicture
      style={{
        display: 'block',
        height: '100%',
        objectFit: 'cover',
        width: '100%',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden'
      }}
      webkit-playsinline='true'
    >
      {hasWebm && <source src={slide.webm} type='video/webm' />}
      <source src={slide.mp4} type='video/mp4' />
    </video>
  )
}

export const VideoRows: FC<Props> = memo(({
  className,
  firstRowVideos,
  secondRowVideos,
  thirdRowVideos
}) => {
  const rootRef = useRef<HTMLElement>(null)

  // SSR-safe default (prevents hydration mismatch)
  const [preloadMode, setPreloadMode] = useState<HTMLVideoElement['preload']>('metadata')
  const preloadModeRef = useRef<HTMLVideoElement['preload']>('metadata')

  useEffect(() => {
    const nextMode: HTMLVideoElement['preload'] = detectSafari() ? 'metadata' : 'none'
    setPreloadMode(nextMode)
    preloadModeRef.current = nextMode
  }, [])

  useAutoPauseOffscreenVideos(rootRef, preloadModeRef)

  const row1 = useMemo(() => buildLoop(firstRowVideos), [firstRowVideos])
  const row2 = useMemo(() => buildLoop(secondRowVideos), [secondRowVideos])
  const row3 = useMemo(() => buildLoop(thirdRowVideos), [thirdRowVideos])

  return (
    <Root
      ref={rootRef}
      className={className}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: .35, ease: 'easeOut' }}
    >
      <Row>
        <Track $duration={LOOP_DURATION_S} $reverse={false}>
          {row1.map((v, idx) => (
            <VideoWrapper key={`r1:${idx}:${v.mp4}`}>
              <AutoVideo slide={v} preload={preloadMode} />
            </VideoWrapper>
          ))}
        </Track>
      </Row>

      <Row>
        <Track $duration={LOOP_DURATION_S} $reverse>
          {row2.map((v, idx) => (
            <VideoWrapper key={`r2:${idx}:${v.mp4}`}>
              <AutoVideo slide={v} preload={preloadMode} />
            </VideoWrapper>
          ))}
        </Track>
      </Row>

      <Row>
        <Track $duration={LOOP_DURATION_S} $reverse={false}>
          {row3.map((v, idx) => (
            <VideoWrapper key={`r3:${idx}:${v.mp4}`}>
              <AutoVideo slide={v} preload={preloadMode} />
            </VideoWrapper>
          ))}
        </Track>
      </Row>
    </Root>
  )
})

VideoRows.displayName = 'VideoRows'

/* -------------------------------------------------------------------------- */
/* Styled components                                                          */
/* -------------------------------------------------------------------------- */

const Root = styled(motion.section)`
  display: grid;
  gap: 20px;
  overflow: hidden;
  width: 100%;
`

const Row = styled.div`
  overflow: hidden;
  width: 100%;
`

const Track = styled.div<{ $duration: number; $reverse: boolean }>`
  display: flex;
  /* gap: 30px; */
  transform: translate3d(0, 0, 0);
  will-change: transform;

  animation-direction: ${({ $reverse }) => ($reverse ? 'alternate-reverse' : 'alternate')};
  animation-duration: ${({ $duration }) => $duration}s;
  animation-iteration-count: infinite;
  animation-name: marquee;
  animation-timing-function: linear;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @keyframes marquee {
    from {
      transform: translate3d(0, 0, 0);
    }

    to {
      transform: translate3d(-50%, 0, 0);
    }
  }
`

const VideoWrapper = styled.div`
  aspect-ratio: 16/9;
  border-radius: 20px;
  contain: paint;
  flex-shrink: 0;
  height: calc(33vh - 20px);
  isolation: isolate;
  margin: 0 15px;
  min-width: 300px;
  opacity: .45;
  overflow: hidden;
  transform: translateZ(0);
  width: auto;

  @media screen and (min-width: 768px) {
    min-width: 430px;
  }

  video {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`
